import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mvpApi } from '../../services/api';
import { getRefFromUrl, captureRefOnVisit, setStoredPartnerToken } from '../../utils/attribution';
import {
  clearQualificationResponses,
  initQualificationResponses,
} from '../../utils/qualificationResponses';
import LanguageSwitch from '../../components/LanguageSwitch';
import AppHeader from '../../components/AppHeader';
import { useProcedure } from '../../procedures/ProcedureContext';
import { useApplicantFlowPaths } from '../../procedures/useApplicantFlowPaths';
import { isProcedureBuilt, pickEn } from '../../procedures/procedureContent';
import '../Landing.css';
import './ProcedureLanding.css';

const DEFAULT_PRICE = 250;
const PARTNER_PRICE_KEY = 'mvp_partner_price';

export default function ProcedureLanding() {
  const navigate = useNavigate();
  const { bundle, loading, error, procedure } = useProcedure();
  const paths = useApplicantFlowPaths();
  const [promoData, setPromoData] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const fetchedRef = useRef(null);
  const heroCtaRef = useRef(null);

  const refParam = getRefFromUrl();
  const token = refParam != null ? String(refParam).trim() : '';
  const hasRef = token !== '';

  useEffect(() => {
    captureRefOnVisit();
  }, []);

  useEffect(() => {
    if (!hasRef || !token) {
      setPromoData(null);
      return;
    }
    if (fetchedRef.current === token) return;
    setPromoLoading(true);
    fetchedRef.current = token;
    mvpApi
      .getPartnerByToken(token)
      .then((res) => {
        const data = res.data;
        setPromoData({
          price: data.price != null ? Number(data.price) : null,
          partner_name: data.partner_name || null,
        });
        if (data.price != null) {
          try {
            sessionStorage.setItem(PARTNER_PRICE_KEY, String(data.price));
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {
        setPromoData(null);
        fetchedRef.current = null;
      })
      .finally(() => setPromoLoading(false));
  }, [hasRef, token]);

  const handleStart = () => {
    if (token) setStoredPartnerToken(token);
    clearQualificationResponses();
    initQualificationResponses();
    navigate(paths.qualify);
  };

  /** Scrolls back to the single "Check eligibility" CTA in the hero, instead of duplicating the action. */
  const scrollToHeroCta = () => {
    heroCtaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (loading) {
    return (
      <div className="landing procedure-landing">
        <AppHeader rightSlot={<LanguageSwitch />} />
        <main className="landing-scroll">
          <div className="procedure-landing-loading">Loading procedure…</div>
        </main>
      </div>
    );
  }

  if (error || !bundle || !procedure) {
    return (
      <div className="landing procedure-landing">
        <AppHeader rightSlot={<LanguageSwitch />} />
        <main className="landing-scroll">
          <div className="procedure-landing-error">{error || 'Procedure unavailable'}</div>
        </main>
      </div>
    );
  }

  const title = pickEn(procedure.title);
  const subtitle = pickEn(procedure.subtitle);
  const overview = pickEn(procedure.overview);
  const distinguish = pickEn(procedure.distinguish_from);
  const jurisdiction = pickEn(procedure.jurisdiction);
  const eligibility = (bundle.card?.eligibility || []).slice(0, 6);
  const built = isProcedureBuilt(bundle.card);
  const reviewGate = pickEn(bundle.card?.review_gate);

  const hasPromo = promoData && (promoData.price != null || promoData.partner_name);
  const displayPrice = hasPromo && promoData.price != null ? promoData.price : DEFAULT_PRICE;
  const showStrikethrough = hasPromo && promoData.price != null && promoData.price < DEFAULT_PRICE;

  return (
    <div className="landing procedure-landing">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="landing-scroll">
        <div className="landing-inner">
          <section className="hero">
            <div className="hero-eyebrow">
              <span className="live-dot" />
              {jurisdiction}{built ? '' : ' · Preview'}
            </div>
            <h1 className="hero-title procedure-hero-title">
              <span className="hero-accent-legal">{title}</span>
            </h1>
            {subtitle ? <p className="procedure-subtitle">{subtitle}</p> : null}
            {overview ? <p className="hero-body">{overview}</p> : null}
            {distinguish ? <p className="procedure-distinguish">{distinguish}</p> : null}
            {reviewGate ? <p className="procedure-review-note">{reviewGate}</p> : null}

            <button type="button" className="price-cta" onClick={handleStart} ref={heroCtaRef}>
              <span>Check eligibility</span>
            </button>
            <p className="cta-subline">Free eligibility check · from €{displayPrice} if you proceed</p>
          </section>

          {eligibility.length > 0 ? (
            <section className="section section-with-border">
              <div className="why-heading">
                <span>Who can</span>
                <span className="why-heading-accent"> qualify</span>
              </div>
              <div className="why-list">
                {eligibility.map((item) => (
                  <div className="why-row" key={item.id}>
                    <div className="wr-icon" aria-hidden="true">✓</div>
                    <div>
                      <div className="wr-title">{pickEn(item.label)}</div>
                      <div className="wr-desc">{pickEn(item.detail)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="price-section section-with-border" aria-label="Pricing">
            <div className="price-heading">
              <span>Get started</span>
              <span className="price-heading-accent"> with Global Mobility</span>
            </div>
            <div className="price-block">
              <div className="price-inner">
                {promoLoading && hasRef ? (
                  <div className="price-loading">Checking offer…</div>
                ) : (
                  <>
                    {showStrikethrough ? (
                      <div className="price-strike" aria-hidden="true">€{DEFAULT_PRICE}</div>
                    ) : null}
                    <div className="price-big">
                      <span className="price-eur">€</span>
                      {displayPrice}
                    </div>
                    <div className="price-note">per applicant</div>
                  </>
                )}
              </div>
              <div className="price-cta-wrap">
                <div className="price-cta-title">Start your application</div>
                <div className="price-cta-desc">
                  Answer a few questions to see if this route fits your situation.
                </div>
                <button type="button" className="price-cta price-cta--secondary" onClick={scrollToHeroCta}>
                  <span>Check eligibility ↑</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <p className="site-footer-copy">© Global Mobility Service Company LLC</p>
        </div>
      </footer>
    </div>
  );
}
